import React, { PureComponent } from 'react'
import { Button, Col, Container, Row, Spinner, Table } from 'react-bootstrap'
import { Subscription, BehaviorSubject, of, concat } from "rxjs"
import { distinctUntilChanged, switchMap, map, catchError } from "rxjs/operators"
import { toast } from "react-toastify"
import { connect } from "react-redux"
import { ColumnFilter } from '../../api/GovDonor/ColumnFilter'
import GridData from '../../api/GovDonor/GridData'
import GridRow from '../../api/GovDonor/GridRow'
import { AppState } from '../../AppState'
import { Filter, Sort } from "../../api/GridRequest"
import ColumnHeaderFilter from '../ColumnHeaderFilter'
import { CustomizableFilterColumn } from '../ColumnsCustomizeModal/CustomizableColumn'
import { AppPageWrapper } from '../NavMenu/AppPageWrapper'
import TablePagination from '../TablePagination'
import AddGovDonorModal from './AddGovDonorModal'
import GovDonorDetailsModal from './GovDonorDetailsModal'
import { GovDonorGridGetObservable } from '../../api/GovDonor/GovDonorGridGetObservable'
import SearchField from '../../api/SearchField'
import SearchBar from '../SearchBar'
import { FaArrowDown, FaArrowUp } from 'react-icons/fa'
import { GetSelectedAssignedClinic } from '../../authentication/SelectedCompany/CompanySelectionDrivenSelectors'
import { Link } from 'react-router-dom'
import { BuildUserAccess } from "../../authentication/BuildUserAccess"
import FormatError from '../../formatters/FormatError'
import { isProcessingCollection, ProcessingCollectionLabel, refreshProcessingCollectionTests } from '../../api/CachedCollectionTest'

type Column = CustomizableFilterColumn<keyof GridRow, keyof ColumnFilter>

interface GovDonorState {
  loading: boolean
  data?: GridData
  details?: GridRow
  detailsModal: boolean
  addDonorModal: boolean
  columns: Column[]
}

interface GovDonorProps {
  canManageGovDonors?: boolean
  company?: number
}

const PageSize = 30

const DefaultSearchFields: SearchField[] = [
  { ID: "EmployerID", Name: "Donor ID" },
  { ID: "FirstName", Name: "First Name" },
  { ID: "LastName", Name: "Last Name" },
  { ID: "AccessCode", Name: "Access Code" }
]

const DefaultColumns: Column[] = [
  { name: "EmployerID", label: "DONOR ID", visible: true },
  { name: "LastName", label: "LAST NAME", visible: true },
  { name: "FirstName", label: "FIRST NAME", visible: true },
  { name: "GovAccessCode", label: "ACCESS CODE", visible: true },
  { name: "GovPoolName", label: "GROUP", visible: true, filter: "GovPoolName" },
  { name: "LastTestDate", label: "LAST TEST DATE", visible: true },
  { name: "Result", label: "RESULT", visible: true },
  { name: "DateScheduled", label: "PENDING RANDOM", visible: true }
]

interface GridSearchParams {
  pageNumber: number
  sort: Sort
  filters: Filter[]
  search: { field: string, text: string }
}

class GovDonorGrid extends PureComponent<GovDonorProps, GovDonorState> {
  pageSubscription?: Subscription

  state: GovDonorState = {
    loading: false,
    detailsModal: false,
    addDonorModal: false,
    columns: DefaultColumns
  }

  page$ = new BehaviorSubject<GridSearchParams>({
    pageNumber: 1,
    sort: { column: "", order: "" },
    filters: [],
    search: { field: "", text: "" }
  })

  sortGrid(column: string, order?: string) {
    const newSort = { column: column, order: order || "" }
    this.page$.next({ ...this.page$.value, sort: newSort })
  }

  filterGrid(column: string, items: string[]) {
    const filters = [...this.page$.value.filters]
    const filter = { column: column, values: items }
    let index = filters.findIndex(item => item.column === filter.column)
    if (index >= 0) {
      filters[index] = filter
    } else {
      filters.push(filter)
    }

    this.page$.next({ ...this.page$.value, pageNumber: 1, filters: filters })
  }

  onSearch = (field: string, text: string) => this.page$.next({ ...this.page$.value, pageNumber: 1, search: { field: field, text: text } })
  handlePageChange = (pageNumber: number) => this.page$.next({ ...this.page$.value, pageNumber: pageNumber })
  showDetailsModal = (details: GridRow) => this.setState({ detailsModal: true, details: details })
  hideDetailsModal = (hasUpdates: boolean) => {
    if (hasUpdates) {
      this.handlePageChange(1)
    }
    this.setState({ detailsModal: false, details: undefined })
  }

  showAddDonor = () => this.setState({ addDonorModal: true })
  hideAddDonor = () => this.setState({ addDonorModal: false })
  reload = () => this.page$.next({ ...this.page$.value, pageNumber: 1 })

  handleCreateSuccessful = () => {
    this.hideAddDonor()
    this.reload()
  }

  componentDidMount() {
    this.pageSubscription = this.page$.pipe(
      distinctUntilChanged(),
      switchMap((params: GridSearchParams) => concat(
        of({ loading: true }),
        GovDonorGridGetObservable(params.pageNumber, PageSize, params.sort, params.filters, params.search, this.props.company).pipe(
          map((result: GridData) => ({ data: result, loading: false })),
          catchError((error) => of({ loading: false, errorMessage: FormatError(error) }))
      )))
    ).subscribe(
      (newState: { loading: boolean, data?: GridData, errorMessage?: string }) => {
        if (newState.data) {
          this.setState({ loading: newState.loading, data: newState.data })
        } else {
          this.setState({ loading: newState.loading })
        }
        newState.errorMessage && toast.error(newState.errorMessage)
      },
      (error) => {
        this.setState({ loading: false })
        toast.error(FormatError(error))
      }
    )
  }

  componentWillUnmount() {
    this.pageSubscription && this.pageSubscription.unsubscribe()
    this.page$.unsubscribe()
  }

  visibleColumns = () => this.state.columns.filter(col => col.visible)

  renderHeader = () => this.visibleColumns().map((col: Column, i) => {
    const sort = this.page$.value.sort
    return (
      <th key={col.name}>
        {this.state.data && col.filter && col.filter in this.state.data && <ColumnHeaderFilter
          title={col.label || col.name}
          filteredItems={this.page$.value.filters.find(item => item.column === col.filter)?.values || []}
          sort={sort.column === col.name ? sort.order === "asc" ? "asc" : "desc" : undefined}
          items={this.state.data[col.filter as keyof ColumnFilter]?.filter(item => !!item) || []}
          onSort={(order?: string) => this.sortGrid(col.name, order)}
          onFilter={(items: string[]) => col.filter && this.filterGrid(col.filter, items)}
        />}
        {(this.state.data && !col.filter) && this.renderHeaderColumn(col.name, col.label || col.name)}
      </th>
    )
  })

  renderHeaderColumn = (columnName: string, title: string) => {
    const sort = this.page$.value.sort
    const setAscSort = sort.order !== "asc"
    return <>
      <div className="filter-toggle">
        <span onClick={() => this.sortGrid(columnName, setAscSort ? "asc" : "desc")} className="column-header-title text-uppercase mr-1">{title}</span>
        <span hidden={sort.column !== columnName || sort.order !== "asc"}><FaArrowUp /></span>
        <span hidden={sort.column !== columnName || sort.order !== "desc"}><FaArrowDown /></span>
      </div>
    </>
  }

  renderRows = () => {
    if (this.state.data?.Records && this.state.data?.Records.length) {
      return this.state.data.Records.map((row: GridRow, i: number) =>
        <tr key={i}>
          <td><Button variant="link" onClick={() => this.showDetailsModal(row)} className="p-0">{row.EmployerID}</Button></td>
          <td>{row.LastName}</td>
          <td>{row.FirstName}</td>
          <td>{row.GovAccessCode}</td>
          <td>{row.GovPoolName}</td>
          <td>{row.LastTestDate}</td>
          <td>{row.Result}</td>
          <td>{row.DateScheduled && (isProcessingCollection(row.TestDetailID) ? ProcessingCollectionLabel : 
            <Link className="btn-link btn-icon-link" to={{ pathname: "/gov-order", state: { formData: [row], pendingRandom: true } }}>Random Test</Link>)}
          </td>
        </tr>)
    } else {
      return <tr><td colSpan={9}>No Records Found</td></tr>
    }
  }

  render() {
    refreshProcessingCollectionTests()
    return (
      <AppPageWrapper parentClassName={"align-items-center"} showNav>
        <Container fluid className="grid-container">
          <Row className="pb-2">
            <Col>
              {this.state.detailsModal && this.state.details && <GovDonorDetailsModal details={this.state.details} onHide={this.hideDetailsModal} />}
              {this.state.addDonorModal && <AddGovDonorModal show onHide={() => this.hideAddDonor()} onSuccess={() => this.handleCreateSuccessful()} />}
              <SearchBar
                allowBlank
                resetButton
                onSearch={this.onSearch}
                searchFields={DefaultSearchFields}
                loading={this.state.loading}
              />
            </Col>
            <Col md={8} className="text-md-right mt-2 mt-md-0">
              <Button variant="primary" size="sm" onClick={this.showAddDonor} className="mr-2">Add Donor</Button>
            </Col>
          </Row>
          <Row>
            <Col>
              <Table bordered responsive>
                <thead>
                  <tr>
                    {this.renderHeader()}
                  </tr>
                </thead>
                <tbody>
                  {
                    this.state.loading ?
                      <tr>
                        <td className="text-center" colSpan={8}>
                          <Spinner animation="border" variant="secondary" />
                        </td>
                      </tr> : this.renderRows()
                  }
                </tbody>
              </Table>
            </Col>
          </Row>
          {this.state.data ?
            <Row>
              <Col>
                <span className="total-records">Total records: {this.state.loading ? " ... " : this.state.data.Total}</span>
                <TablePagination pageNumber={this.page$.value.pageNumber} pages={this.state.data.Pages} onChange={this.handlePageChange} />
              </Col>
            </Row> : undefined}
        </Container>
      </AppPageWrapper>
    )
  }
}

function mapStateToProps(state: AppState): GovDonorProps {
  const userAccess = BuildUserAccess(state);

  return {
    canManageGovDonors: userAccess.CanManageGovDonors,
    company: GetSelectedAssignedClinic(state)?.AccountID
  }
}
const ConnectedGovDonor = connect(mapStateToProps, null)(GovDonorGrid)
export default ConnectedGovDonor
